import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { call } from "./api";
import { uploadPackingSlip } from "./orderEntry";

// The customer's shipments (docs/api.md §9.26, docs/partner-portal.md §13).

/** Why an order can no longer be changed or cancelled in the portal (N7). */
export type NotChangeable = "closed" | "elsewhere" | "planned" | "pending" | "transition";

export const VIEWS = ["active", "waiting", "delivered", "all"] as const;
export type View = (typeof VIEWS)[number];

export type ShipmentStatus = "waiting" | "rejected" | "confirmed" | "planned" | "in_transit" | "delivered" | "refused" | "cancelled";
export type WaitingReason = "address" | "stops" | "duplicate" | "too_late" | "account" | "change" | "announced" | "check";

export interface Place {
  name: string;
  city: string;
  countryCode: string;
  windowStart: string;
  windowEnd: string;
}

export interface ShipmentRow {
  number: string;
  orderNumber: string;
  reference: string | null;
  status: ShipmentStatus;
  issue: "shortage" | "damage" | null;
  load: Place | null;
  unload: (Place & { late: boolean }) | null;
  pallets: number | null;
  weightKg: number | null;
  /** The signed CMR: in (to download), missing (ended without one yet), or none (not delivered yet). */
  cmr: "available" | "none" | "missing";
  cmrId: string | null;
}

export interface Step {
  done: boolean;
  at: string | null;
}

export interface FullPlace extends Place {
  street: string | null;
  houseNumber: string | null;
  houseNumberAddition: string | null;
  postalCode: string | null;
  reference: string | null;
  actualAt: string | null;
}

export interface Shipment extends ShipmentRow {
  description: string | null;
  issueRemark: string | null;
  waitingReasons: WaitingReason[];
  enteredVia: "portal" | "edi" | "willems";
  enteredAt: string;
  /** The colleague who typed it in the portal; null for an order that came in another way. */
  enteredBy: string | null;
  /** Wijzigen and Annuleren: an order typed in the portal, until it is planned (N7). */
  canChange: boolean;
  /** Why not; "transition": typed before 1 December (while TALIS got a copy), changed through Willems. */
  notChangeableBecause: NotChangeable | null;
  steps: Record<"entered" | "confirmed" | "planned" | "loaded" | "delivered" | "completed", Step>;
  load: FullPlace | null;
  unload: (FullPlace & { late: boolean }) | null;
  cargo: {
    description: string | null;
    pallets: number | null;
    weightKg: number | null;
    loadingMeters: number | null;
    adr: boolean;
    temperatureMinC: number | null;
    temperatureMaxC: number | null;
    requirements: string[];
  };
  /** The signed CMR first, then the packing slips the customer added when it entered the order (§17). */
  documents: { kind: "cmr" | "packing_slip"; id: string; name: string; size: number; mimeType: string; receivedAt: string }[];
  /** Pakbon toevoegen after sending (§17a): until it is unloaded, at most two per shipment. */
  canAddPackingSlip: boolean;
  /** Packing slips just added, on their way to the dossier: by name, not downloadable yet. */
  pendingPackingSlips: string[];
}

/** The columns the customer sorts and filters on (docs/partner-portal.md §13a): every one but the CMR's own icon. */
export const COLUMNS = ["number", "from", "to", "load", "unload", "cargo", "status", "cmr"] as const;
export type Column = (typeof COLUMNS)[number];

export interface ListFilter {
  view: View;
  q?: string;
  from?: string;
  to?: string;
  /** A column the customer clicked; without it today and later first, then the days before (K2). */
  sort?: Column;
  order?: "asc" | "desc";
  /** Per column its filter, written as the API reads it (components/ColumnHead.tsx). */
  filters?: Partial<Record<Column, string>>;
}

/** The period only counts for delivered and all, as the screen offers it. */
function params(filter: ListFilter, extra: Record<string, string | undefined> = {}): string {
  const search = new URLSearchParams();
  search.set("view", filter.view);
  if (filter.q?.trim()) search.set("q", filter.q.trim());
  if (filter.view === "delivered" || filter.view === "all") {
    if (filter.from) search.set("from", filter.from);
    if (filter.to) search.set("to", filter.to);
  }
  if (filter.sort) {
    search.set("sort", filter.sort);
    search.set("order", filter.order ?? "asc");
  }
  for (const column of COLUMNS) {
    const value = filter.filters?.[column];
    if (value) search.set(`filter.${column}`, value);
  }
  for (const [key, value] of Object.entries(extra)) if (value) search.set(key, value);
  return search.toString();
}

interface Page {
  data: ShipmentRow[];
  pagination: { limit: number; hasNextPage: boolean; nextCursor: string | null };
}

export function shipmentsQuery(filter: ListFilter) {
  return infiniteQueryOptions({
    queryKey: ["partner", "shipments", filter],
    queryFn: ({ pageParam }) => call<Page>("GET", `/partner/shipments?${params(filter, { cursor: pageParam ?? undefined })}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.pagination.hasNextPage ? last.pagination.nextCursor : null),
  });
}

export function countsQuery(filter: Pick<ListFilter, "from" | "to">) {
  const search = new URLSearchParams();
  if (filter.from) search.set("from", filter.from);
  if (filter.to) search.set("to", filter.to);
  return queryOptions({
    queryKey: ["partner", "shipments", "counts", filter.from ?? "", filter.to ?? ""],
    queryFn: () => call<Record<View, number>>("GET", `/partner/shipments/counts?${search.toString()}`),
  });
}

export function shipmentQuery(number: string) {
  return queryOptions({
    queryKey: ["partner", "shipment", number],
    queryFn: () => call<Shipment>("GET", `/partner/shipments/${encodeURIComponent(number)}`),
    // A packing slip just added is in the dossier a few seconds later: look again until it is.
    refetchInterval: (query) => ((query.state.data?.pendingPackingSlips.length ?? 0) > 0 ? 3000 : false),
  });
}

/** A packing slip added to a shipment after sending (§17a): the pdf first, as with an order, then tied to it. */
export async function addShipmentPackingSlip(number: string, file: File): Promise<{ name: string }> {
  const uploaded = await uploadPackingSlip(file);
  return call<{ name: string }>("POST", `/partner/shipments/${encodeURIComponent(number)}/packing-slips`, { fileId: uploaded.id });
}

/** The address of the Excel file for what the filter shows. */
export function csvHref(filter: ListFilter): string {
  return `/api/v1/partner/shipments.csv?${params(filter)}`;
}

/** The signed CMR of one consignment, as a download (docs/api.md §9.26). */
export function documentHref(number: string, id: string): string {
  return `/api/v1/partner/shipments/${encodeURIComponent(number)}/documents/${encodeURIComponent(id)}`;
}

/** The API puts at most this many CMRs in one ZIP (W5). */
export const CMR_ZIP_MAX = 100;

/** The signed CMRs of a selection as one ZIP, named by consignment and reference. */
export function cmrZipHref(numbers: string[]): string {
  return `/api/v1/partner/shipments/cmr.zip?${new URLSearchParams({ numbers: numbers.join(",") }).toString()}`;
}

/** The variant of .wl-status for each state (handoff §5): six of the eight, no new colours. */
export const STATUS_VARIANT: Record<ShipmentStatus, string> = {
  waiting: "onhold",
  rejected: "exception",
  confirmed: "planned",
  planned: "planned",
  in_transit: "intransit",
  delivered: "delivered",
  refused: "exception",
  cancelled: "cancelled",
};
